<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Reconciliation</h1>
				<p class="text-muted mb-0">
					Discrepancies read across checks and by direction — where the shelf
					and the ledger disagree.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div class="card">
			<div class="card-body">
				<h2 class="h5 mb-0">Discrepancies</h2>
				<p class="text-muted mb-3 small">
					Every counted line whose count differed from the ledger, newest
					findings from the checks that produced them. A short line is product
					gone from the shelf; an over line is more found than expected.
				</p>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div v-else-if="rows.length === 0" class="text-center py-4 text-muted">
					No discrepancies recorded. Every count has matched its ledger.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Check</th>
								<th scope="col">Product</th>
								<th scope="col">Location</th>
								<th scope="col" class="text-end">Counted</th>
								<th scope="col" class="text-end">Expected</th>
								<th scope="col" class="text-end">Variance</th>
								<th scope="col">Direction</th>
								<th scope="col">Reason</th>
								<th scope="col">Found</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in rows" :key="row.id">
								<td>
									<RouterLink
										class="font-monospace"
										:to="`/_/inventory/checks/${row.inventory_check_id}`"
									>
										#{{ row.inventory_check_id }}
									</RouterLink>
								</td>
								<td>{{ row.product_name }}</td>
								<td>{{ row.location_name }}</td>
								<td class="text-end">{{ formatAmount(row.counted_amount) }}</td>
								<td class="text-end">
									{{ formatAmount(row.expected_amount) }}
								</td>
								<td
									class="text-end fw-semibold"
									:class="
										row.variance < 0
											? 'text-danger'
											: row.variance > 0
												? 'text-warning-emphasis'
												: ''
									"
								>
									{{ formatVariance(row.variance) }}
								</td>
								<td>
									<span
										class="badge rounded-pill"
										:class="
											row.variance < 0
												? 'bg-danger'
												: row.variance > 0
													? 'bg-warning text-dark'
													: 'bg-secondary'
										"
									>
										{{ directionLabel(row.variance) }}
									</span>
								</td>
								<td class="text-muted">{{ row.reason || "—" }}</td>
								<td>
									<TimeRelativeWithTooltip :time="row.created" />
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";

import { apiClient } from "@/client";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type { InventoryReconciliation } from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const rows = ref<InventoryReconciliation[]>([]);
const loading = ref(false);
const error = ref("");

function formatAmount(value: number): string {
	if (Number.isInteger(value)) return String(value);
	return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

// The sign states the direction: a negative variance is short of the ledger, a
// positive one is over it.
function formatVariance(value: number): string {
	if (value === 0) return "0";
	const magnitude = formatAmount(Math.abs(value));
	return value > 0 ? `+${magnitude}` : `-${magnitude}`;
}

function directionLabel(value: number): string {
	if (value < 0) return "Short";
	if (value > 0) return "Over";
	return "Even";
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		// Every discrepancy through the generic QUERY endpoint (the legacy GET
		// list's replacement, doc/in-progress/query.md §11 Phase 4): the page rows
		// are the same shape and the walk holds the whole findings set.
		const page = await apiClient.JSONQueryAll<InventoryReconciliation>(
			"/api/inventory/reconciliation",
		);
		rows.value = page.items;
	} catch (e: any) {
		error.value = e?.message || "Failed to load the reconciliation.";
		showNotification("Failed to load reconciliation", error.value);
	} finally {
		loading.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
